"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";
import type { ChartCandle } from "@/app/api/live/chart/route";
import type { LiveData } from "@/contexts/LiveDataContext";
import { isLiveChartRoute, liveChartDefaultRange } from "@/constants/priceHistory";
import {
  chartDataMatchesRange,
  fetchChartCandlesClient,
  resolveSocketWsUrl,
  sortChartCandles,
  type ChartTimeRange,
} from "@/lib/chartCandlesClient";
import { reportError } from "@/lib/reportError";

export type TimeRange = ChartTimeRange;

export type LiveChartTickListener = (data: LiveData) => void;

function applyChartPayload(
  data: ChartCandle[],
  range: TimeRange,
  refs: {
    chartDataRangeRef: React.MutableRefObject<TimeRange | null>;
    isChartLoadingRef: React.MutableRefObject<boolean>;
  },
  setters: {
    setChartCandles: (candles: ChartCandle[]) => void;
    setChartDataRange: (range: TimeRange | null) => void;
    setIsChartLoading: (loading: boolean) => void;
  },
): boolean {
  const sorted = sortChartCandles(data);
  if (!chartDataMatchesRange(sorted, range)) return false;
  setters.setChartCandles(sorted);
  refs.chartDataRangeRef.current = range;
  setters.setChartDataRange(range);
  refs.isChartLoadingRef.current = false;
  setters.setIsChartLoading(false);
  return true;
}

interface ChartWebSocketContextType {
  chartCandles: ChartCandle[];
  /** Range the current `chartCandles` were loaded for; null while switching. */
  chartDataRange: TimeRange | null;
  isChartLoading: boolean;
  activeRange: TimeRange;
  setTimeRange: (range: TimeRange) => void;
  /** Hydrate chart state from SSR props (same Binance fetch path as /api/live/chart). */
  seedChartData: (range: TimeRange, candles: ChartCandle[]) => void;
}

interface LiveWebSocketContextType {
  liveData: LiveData | null;
  lastUpdatedAt: Date | null;
  isConnected: boolean;
}

/** @deprecated Prefer useChartWebSocket / useLiveWebSocket to avoid chart re-renders on price ticks. */
interface WebSocketContextType extends ChartWebSocketContextType, LiveWebSocketContextType {}

const ChartWebSocketContext = createContext<ChartWebSocketContextType | null>(null);
const LiveWebSocketContext = createContext<LiveWebSocketContextType | null>(null);
const WebSocketContext = createContext<WebSocketContextType | null>(null);

interface WebSocketProviderProps {
  children: ReactNode;
}

const DEFAULT_LIVE_RANGE: TimeRange = "1D";

export function WebSocketProvider({ children }: WebSocketProviderProps): React.ReactElement {
  const pathname = usePathname();
  const isLiveChartPage = isLiveChartRoute(pathname);
  const [chartCandles, setChartCandles] = useState<ChartCandle[]>([]);
  const [liveData, setLiveData] = useState<LiveData | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [activeRange, setActiveRange] = useState<TimeRange>(() =>
    liveChartDefaultRange(pathname),
  );
  const [chartDataRange, setChartDataRange] = useState<TimeRange | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const activeRangeRef = useRef<TimeRange>(liveChartDefaultRange(pathname));
  const chartDataRangeRef = useRef<TimeRange | null>(null);
  const pendingRangeRef = useRef<TimeRange | null>(null);
  const pausedForCacheRef = useRef(false);
  const isChartLoadingRef = useRef(true);
  const isLiveChartPageRef = useRef(isLiveChartPage);
  isLiveChartPageRef.current = isLiveChartPage;

  const fetchChartFromApi = useCallback((range: TimeRange) => {
    fetchChartCandlesClient(range)
      .then((data) => {
        if (!isLiveChartPageRef.current || activeRangeRef.current !== range) return;
        applyChartPayload(
          data,
          range,
          { chartDataRangeRef, isChartLoadingRef },
          { setChartCandles, setChartDataRange, setIsChartLoading },
        );
      })
      .catch((err) => {
        reportError("[Chart] fetch failed:", err, { range });
      });
  }, []);

  const seedChartData = useCallback((range: TimeRange, candles: ChartCandle[]) => {
    if (!isLiveChartPageRef.current || activeRangeRef.current !== range) return;
    if (!candles.length) return;
    applyChartPayload(
      candles,
      range,
      { chartDataRangeRef, isChartLoadingRef },
      { setChartCandles, setChartDataRange, setIsChartLoading },
    );
  }, []);

  const setTimeRange = useCallback((range: TimeRange) => {
    activeRangeRef.current = range;
    setActiveRange(range);
    isChartLoadingRef.current = true;
    setIsChartLoading(true);
    setChartCandles([]);
    chartDataRangeRef.current = null;
    setChartDataRange(null);

    fetchChartFromApi(range);

    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "range", range }));
    } else {
      pendingRangeRef.current = range;
    }
  }, [fetchChartFromApi]);

  useEffect(() => {
    let reconnectTimer: ReturnType<typeof setTimeout>;
    let cancelled = false;
    let currentWs: WebSocket | null = null;

    const resetChartState = () => {
      setChartCandles([]);
      chartDataRangeRef.current = null;
      setChartDataRange(null);
      isChartLoadingRef.current = true;
      setIsChartLoading(true);
    };

    const BASE_DELAY = 1_500;
    const MAX_DELAY = 30_000;
    const MAX_RETRIES = 20;
    let retryCount = 0;

    function getBackoffDelay() {
      const delay = Math.min(BASE_DELAY * Math.pow(2, retryCount), MAX_DELAY);
      return delay;
    }

    function disconnect({ forCache = false }: { forCache?: boolean } = {}) {
      if (forCache) pausedForCacheRef.current = true;
      clearTimeout(reconnectTimer);
      const ws = currentWs;
      currentWs = null;
      wsRef.current = null;
      if (ws) {
        ws.onclose = null;
        ws.onerror = null;
        ws.close();
      }
      setIsConnected(false);
    }

    function resumeConnection() {
      pausedForCacheRef.current = false;
      cancelled = false;
      retryCount = 0;
      if (!currentWs || currentWs.readyState !== WebSocket.OPEN) {
        connect();
      }
    }

    function connect() {
      if (pausedForCacheRef.current || cancelled) return;
      if (retryCount >= MAX_RETRIES) {
        reportError(
          "[WS] Max reconnection attempts reached",
          new Error("WebSocket max reconnection attempts reached"),
          { retries: MAX_RETRIES },
        );
        return;
      }
      const wsUrl = resolveSocketWsUrl(process.env.NEXT_PUBLIC_ZCASH_SOCKET_URL ?? "");
      if (!wsUrl) return;

      const ws = new WebSocket(wsUrl);
      currentWs = ws;

      ws.onopen = () => {
        if (cancelled) {
          ws.close();
          return;
        }
        wsRef.current = ws;
        setIsConnected(true);

        const range = isLiveChartPageRef.current
          ? (pendingRangeRef.current ?? activeRangeRef.current)
          : DEFAULT_LIVE_RANGE;
        ws.send(JSON.stringify({ type: "range", range }));
        pendingRangeRef.current = null;
      };

      ws.onmessage = (event: MessageEvent) => {
        if (cancelled) return;
        retryCount = 0;
        try {
          const msg = JSON.parse(event.data as string);
          if (msg.type === "chart" && Array.isArray(msg.data)) {
            if (!isLiveChartPageRef.current) return;
            applyChartPayload(
              msg.data,
              activeRangeRef.current,
              { chartDataRangeRef, isChartLoadingRef },
              { setChartCandles, setChartDataRange, setIsChartLoading },
            );
          } else if (msg.type === "live" && msg.data) {
            const nextLive = { ...msg.data };
            setLiveData(nextLive);
            if (nextLive.last_updated_at) {
              setLastUpdatedAt(new Date(nextLive.last_updated_at));
            }
            if (
              isLiveChartPageRef.current &&
              !isChartLoadingRef.current &&
              chartDataRangeRef.current === activeRangeRef.current
            ) {
              liveChartTickListeners.forEach((listener) => listener(nextLive));
            }
          }
        } catch (err) {
          reportError("WS parse error:", err);
        }
      };

      ws.onerror = () => {
        if (!cancelled) setIsConnected(false);
      };

      ws.onclose = () => {
        if (cancelled || pausedForCacheRef.current) return;
        wsRef.current = null;
        setIsConnected(false);
        const delay = getBackoffDelay();
        retryCount++;
        reconnectTimer = setTimeout(connect, delay);
      };
    }

    function handlePageHide() {
      disconnect({ forCache: true });
    }

    function handlePageShow(event: PageTransitionEvent) {
      if (event.persisted || document.visibilityState === "visible") {
        resumeConnection();
      }
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        disconnect({ forCache: true });
      } else {
        resumeConnection();
      }
    }

    function handleFreeze() {
      disconnect({ forCache: true });
    }

    const scheduleConnect = () => {
      if (isLiveChartPageRef.current) {
        connect();
        return;
      }
      if (typeof requestIdleCallback === "function") {
        requestIdleCallback(() => connect(), { timeout: 2500 });
      } else {
        window.setTimeout(connect, 1);
      }
    };

    scheduleConnect();
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("freeze", handleFreeze);

    return () => {
      cancelled = true;
      pausedForCacheRef.current = false;
      clearTimeout(reconnectTimer);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("freeze", handleFreeze);
      disconnect();
      resetChartState();
      setLiveData(null);
      setLastUpdatedAt(null);
    };
  }, []);

  useEffect(() => {
    if (!isLiveChartPage) {
      setChartCandles([]);
      chartDataRangeRef.current = null;
      setChartDataRange(null);
      isChartLoadingRef.current = true;
      setIsChartLoading(true);
      return;
    }

    const rangeForPage = liveChartDefaultRange(pathname);
    activeRangeRef.current = rangeForPage;
    setActiveRange(rangeForPage);
    pendingRangeRef.current = rangeForPage;

    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "range", range: rangeForPage }));
    }
  }, [isLiveChartPage, pathname]);

  useEffect(() => {
    if (!isLiveChartPage || !isChartLoading) return;
    fetchChartFromApi(activeRange);
  }, [isLiveChartPage, isChartLoading, activeRange, fetchChartFromApi]);

  const chartValue = useMemo(
    () => ({
      chartCandles,
      chartDataRange,
      isChartLoading,
      activeRange,
      setTimeRange,
      seedChartData,
    }),
    [chartCandles, chartDataRange, isChartLoading, activeRange, setTimeRange, seedChartData],
  );

  const liveValue = useMemo(
    () => ({
      liveData,
      lastUpdatedAt,
      isConnected,
    }),
    [liveData, lastUpdatedAt, isConnected],
  );

  const legacyValue = useMemo(
    () => ({ ...chartValue, ...liveValue }),
    [chartValue, liveValue],
  );

  return (
    <ChartWebSocketContext.Provider value={chartValue}>
      <LiveWebSocketContext.Provider value={liveValue}>
        <WebSocketContext.Provider value={legacyValue}>{children}</WebSocketContext.Provider>
      </LiveWebSocketContext.Provider>
    </ChartWebSocketContext.Provider>
  );
}

const liveChartTickListeners = new Set<LiveChartTickListener>();

/** Imperative chart updates — avoids re-rendering chart consumers on each price tick. */
export function subscribeLiveChartTick(listener: LiveChartTickListener): () => void {
  liveChartTickListeners.add(listener);
  return () => {
    liveChartTickListeners.delete(listener);
  };
}

export function useChartWebSocket(): ChartWebSocketContextType {
  const context = useContext(ChartWebSocketContext);
  if (context === null) {
    throw new Error("useChartWebSocket must be used within a WebSocketProvider");
  }
  return context;
}

export function useLiveWebSocket(): LiveWebSocketContextType {
  const context = useContext(LiveWebSocketContext);
  if (context === null) {
    throw new Error("useLiveWebSocket must be used within a WebSocketProvider");
  }
  return context;
}

export function useWebSocket(): WebSocketContextType {
  const context = useContext(WebSocketContext);
  if (context === null) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
}

export function useWebSocketOptional(): WebSocketContextType | null {
  return useContext(WebSocketContext);
}

export function useLiveWebSocketOptional(): LiveWebSocketContextType | null {
  return useContext(LiveWebSocketContext);
}
